import './style.css';
import { setAssetBase, setAssetVersion, versionedUrl } from './asset-url.js';
import { createAudioController } from './audio.js';
import { createBookController } from './book.js';
import { initDebugConsole } from './debug-console.js';
import { getHomeLink, getLanguageLinks, persistLanguage, resolveLanguage, UI_LABELS } from './i18n.js';
import { safeExternalHref } from './templates.js';

let activeApp = null;

// Read through a variable on purpose. Vite rewrites the literal pattern
// `new URL(<path>, import.meta.url)` into a build-time asset lookup; with a
// template literal that lookup compiles to an empty map, so in the production
// bundle the content URL resolved to `undefined` and the app fetched
// `/undefined`. `data/` is copied by publicDir and is not an asset graph entry,
// so the resolution has to stay a plain runtime one.
const BUNDLE_URL = import.meta.url;

// Page scans, QR overlays and audio sit next to the bundle, so wherever
// book.js was loaded from is where they are. Mounted into Tilda the script
// runs on another origin entirely, and in an object store it runs under a
// bucket prefix; in both the `/assets/…` written into the content JSON would
// resolve against the wrong host. Dev is the exception: there the bundle is
// /src/main.js while public/ is served at the root.
setAssetBase(
  import.meta.env?.DEV ? new URL('/', window.location.href).href : new URL('./', BUNDLE_URL).href
);

// `?debug` puts the console on the screen of the device. It exists for iOS
// testing, where no inspector is reachable from a Windows machine; without the
// flag nothing is created and nothing is patched.
if (new URLSearchParams(window.location.search).has('debug')) initDebugConsole();

/**
 * Resolve where the content JSON actually lives.
 *
 * A root-relative `/data/...` only works when the app is served from the same
 * origin root. Mounted into a Tilda page the script runs on the Tilda domain
 * while the bundle is hosted elsewhere, so that path would always 404.
 *
 * Resolution order:
 *   1. `data-content-url-<lang>` — an explicit per-locale URL. Needed when the
 *      files sit in Tilda's own file manager, where every upload gets its own
 *      unrelated CDN URL and no directory can be constructed.
 *   2. `data-content-base` — a directory holding `book.<lang>.json`.
 *   3. The bundle's own location, for a normal static host deployment.
 *   4. The dev server root.
 */
function resolveContentUrl(mount, language) {
  const explicit = mount?.dataset?.[`contentUrl${language.charAt(0).toUpperCase()}${language.slice(1)}`];
  if (explicit) {
    const safe = safeExternalHref(explicit);
    if (safe) return safe;
  }

  const base = mount?.dataset?.contentBase;
  if (base) {
    const normalised = base.endsWith('/') ? base : `${base}/`;
    return new URL(`book.${language}.json`, new URL(normalised, window.location.href)).href;
  }

  if (import.meta.env?.DEV) return `/data/book.${language}.json`;

  return new URL(`./data/book.${language}.json`, BUNDLE_URL).href;
}

async function loadBookData(mount, language) {
  const response = await fetch(versionedUrl(resolveContentUrl(mount, language)), {
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`Failed to load book content: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

function createButton(label, className) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.textContent = label;
  button.setAttribute('aria-label', label);
  return button;
}

function buildShell(mount, language, labels) {
  mount.classList.add('interactive-book');
  mount.replaceChildren();

  const root = document.createElement('section');
  root.className = 'book-scene';
  root.setAttribute('aria-label', 'Interactive book');
  if (mount.dataset.standalone === 'true') root.dataset.standalone = 'true';
  // Visual only: the scene takes the whole viewport. Kept apart from
  // `standalone`, which additionally hands the app the page title.
  if (mount.dataset.fill === 'viewport') root.dataset.fill = 'viewport';

  // Audio stays out of the frame for now — the final control panel is a later
  // iteration — but remains in the DOM and in tab order, surfacing on
  // :focus-visible. Reading controls are visible, just very quiet.
  const topbar = document.createElement('nav');
  topbar.className = 'book-topbar book-chrome--quiet';
  topbar.setAttribute('aria-label', 'Book controls');

  // The language menu is visible chrome: top centre, styled like the footer
  // navigation. Real links — production locales live on separate URLs.
  const langbar = document.createElement('nav');
  langbar.className = 'book-langbar';
  langbar.setAttribute('aria-label', 'Language');

  // The studio wordmark, riding the same line as the language menu and dressed
  // the same way. It is the only route from the book back into the site: the
  // page deliberately carries no Tilda header, so without this a reader who
  // arrived from search reaches the book and has nowhere to go. Spelled as the
  // book itself spells it, untranslated, in every locale.
  const wordmark = document.createElement('a');
  wordmark.className = 'book-wordmark';
  wordmark.href = getHomeLink(mount);
  wordmark.textContent = 'INSIGHT ADB';
  wordmark.setAttribute('aria-label', labels.home);

  const languageGroup = document.createElement('div');
  languageGroup.className = 'language-switcher';
  const links = getLanguageLinks(mount);

  for (const code of ['ru', 'en', 'es']) {
    const link = document.createElement('a');
    link.className = 'language-switcher__link';
    link.href = links[code];
    link.textContent = code.toUpperCase();
    link.hreflang = code;
    link.lang = code;
    if (code === language) {
      link.setAttribute('aria-current', 'page');
      link.classList.add('is-active');
    }
    link.addEventListener('click', () => persistLanguage(code));
    languageGroup.append(link);
  }

  langbar.append(wordmark, languageGroup);

  const utilityGroup = document.createElement('div');
  utilityGroup.className = 'book-utilities';
  const sfxToggle = createButton(labels.soundOff, 'book-control book-control--audio');
  const musicToggle = createButton(labels.musicOn, 'book-control book-control--audio');
  utilityGroup.append(sfxToggle, musicToggle);

  topbar.append(utilityGroup);

  // The mode toggle is quiet chrome like the topbar: gone from the frame,
  // still in tab order and revealed on focus. Touch tiers keep it visible —
  // there is no keyboard there, so a hidden control would be unreachable.
  const modebar = document.createElement('div');
  modebar.className = 'book-modebar book-chrome--quiet';
  const modeToggle = createButton(labels.reader, 'book-control book-control--mode');
  modeToggle.setAttribute('aria-pressed', 'false');
  modebar.append(modeToggle);

  const viewport = document.createElement('div');
  viewport.className = 'book-viewport';
  viewport.setAttribute('aria-live', 'polite');

  const footer = document.createElement('div');
  footer.className = 'book-footer';
  const previous = createButton(labels.previous, 'book-nav book-nav--previous');
  previous.textContent = '←';
  const progress = document.createElement('output');
  progress.className = 'book-progress';
  progress.setAttribute('aria-live', 'polite');
  const next = createButton(labels.next, 'book-nav book-nav--next');
  next.textContent = '→';
  footer.append(previous, progress, next);

  root.append(topbar, langbar, modebar, viewport, footer);
  mount.append(root);

  return { root, viewport, previous, next, progress, modeToggle, sfxToggle, musicToggle };
}

/**
 * Only on our own preview pages. On Tilda the page title and description are
 * Tilda's — they are what the canonical URL, the search result and the link
 * preview are built from, and a script that rewrites them after load would
 * silently take over the page's SEO shell with a title meant for a local
 * preview.
 */
function updateDocumentMeta(mount, data) {
  if (mount.dataset.standalone !== 'true') return;
  if (data.meta?.title) document.title = data.meta.title;
  const description = document.querySelector('meta[name="description"]');
  if (description && data.meta?.description) description.setAttribute('content', data.meta.description);
}

function wireAudioControls(elements, audio, labels) {
  function refresh() {
    const state = audio.state;
    elements.sfxToggle.disabled = !state.hasSfx;
    elements.musicToggle.disabled = !state.hasMusic;
    elements.sfxToggle.textContent = state.sfxEnabled ? labels.soundOff : labels.soundOn;
    elements.musicToggle.textContent = state.musicEnabled ? labels.musicOff : labels.musicOn;
    elements.sfxToggle.setAttribute('aria-pressed', String(state.sfxEnabled));
    elements.musicToggle.setAttribute('aria-pressed', String(state.musicEnabled));
  }

  async function activateAudio() {
    await audio.unlockFromGesture();
    refresh();
  }

  async function onSfx() {
    await activateAudio();
    audio.setSfxEnabled(!audio.state.sfxEnabled);
    refresh();
  }

  async function onMusic() {
    await activateAudio();
    await audio.setMusicEnabled(!audio.state.musicEnabled);
    refresh();
  }

  elements.sfxToggle.addEventListener('click', onSfx);
  elements.musicToggle.addEventListener('click', onMusic);
  elements.root.addEventListener('pointerdown', activateAudio, { once: true, passive: true });
  refresh();

  return () => {
    elements.sfxToggle.removeEventListener('click', onSfx);
    elements.musicToggle.removeEventListener('click', onMusic);
  };
}

function renderFatalError(mount, error) {
  mount.replaceChildren();
  const message = document.createElement('div');
  message.className = 'book-error';
  message.setAttribute('role', 'alert');
  message.textContent = 'Не удалось загрузить интерактивную книгу. Используйте текстовую версию страницы или повторите попытку позже.';
  mount.append(message);
  console.error(error);
}

export async function initBook(mount = document.querySelector('#book-app')) {
  if (!mount) return null;
  if (activeApp) activeApp.destroy();

  // The release tag rides in from the embed block; see setAssetVersion.
  setAssetVersion(mount.dataset.version);

  const language = resolveLanguage(mount);
  const labels = UI_LABELS[language];

  try {
    const data = await loadBookData(mount, language);
    persistLanguage(language);
    updateDocumentMeta(mount, data);

    const elements = buildShell(mount, language, labels);
    const audio = createAudioController(data.audio);
    const unwireAudio = wireAudioControls(elements, audio, labels);
    const book = createBookController({ data, elements, audio, labels });

    activeApp = {
      destroy() {
        book.destroy();
        unwireAudio();
        audio.destroy();
        activeApp = null;
      }
    };

    return activeApp;
  } catch (error) {
    renderFatalError(mount, error);
    return null;
  }
}

export function destroyBook() {
  activeApp?.destroy();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void initBook(), { once: true });
} else {
  void initBook();
}
